/**
 * WeatherReportCard.js
 *
 * FT-161 Phase 3: Structured weather report card for active voyage log
 * Renders weather entries as a grid layout instead of flat text.
 *
 * Handles both data formats:
 * - Format 1 (departure weather): nested in entry.data.*
 * - Format 2 (in-voyage weather): flat fields on entry.*
 */

import React from 'react';
import { CloudSun } from 'lucide-react';

const WeatherReportCard = ({ entry }) => {
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  // Extract data - handle both nested (data.*) and flat formats
  const windDirection = entry.data?.windDirection || entry.windDirection || null;
  const windSpeed = entry.data?.windSpeed || entry.windSpeed || null;
  const seaState = entry.data?.seaState || entry.seaState || null;
  const visibility = entry.data?.visibility || entry.visibility || null;
  const notes = entry.data?.notes || entry.notes || null;

  // Get recorder name - handle different attribution formats
  const recorder = entry.recordedBy || entry.createdBy?.name || entry.data?.recordedBy || 'Unknown';

  // Format wind display (direction + speed)
  const formatWind = () => {
    if (!windDirection && !windSpeed) return '---';

    const parts = [];
    if (windDirection) parts.push(windDirection);
    if (windSpeed !== null && windSpeed !== undefined) {
      // Handle numeric (knots) or text (moderate/strong) speed
      if (typeof windSpeed === 'number') {
        parts.push(`${windSpeed}kt`);
      } else {
        parts.push(windSpeed);
      }
    }
    return parts.join(' ') || '---';
  };

  // Capitalize first letter for display
  const capitalize = (str) => {
    if (!str) return '---';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border-l-4 border-amber-500 p-4 mx-4 my-2">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CloudSun className="h-5 w-5 text-amber-500" />
          <span className="text-base font-semibold text-gray-900">
            Weather Report
          </span>
        </div>
        <span className="text-sm text-gray-400 font-mono">
          {formatTime(entry.timestamp)}
        </span>
      </div>

      {/* Data Grid - 3 columns */}
      <div className="grid grid-cols-3 gap-4 py-2">
        {/* Wind */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            Wind
          </p>
          <p className="text-sm font-medium text-gray-900">
            {formatWind()}
          </p>
        </div>

        {/* Sea State */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            Sea State
          </p>
          <p className="text-sm font-medium text-gray-900">
            {capitalize(seaState)}
          </p>
        </div>

        {/* Visibility */}
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
            Visibility
          </p>
          <p className="text-sm font-medium text-gray-900">
            {capitalize(visibility)}
          </p>
        </div>
      </div>

      {/* Notes (if present) */}
      {notes && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <p className="text-sm text-gray-600 italic">
            {notes}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-400">Recorded by: {recorder}</span>
      </div>
    </div>
  );
};

export default WeatherReportCard;
